import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { MAX_PINNED_TURN_SKILLS } from '@aline/common';
import { link, routes } from '@aline/routes';
import { ButtonBase, IconButton, Typography } from '@mui/material';
import { MentionInputPlugin } from '@platejs/mention/react';
import clsx from 'clsx';
import { ArrowLeft, Check, FilePlus, Plus, Settings, Sparkles } from 'lucide-react';
import { matchSorter } from 'match-sorter';
import type { TElement } from 'platejs';
import type { PlateElementProps } from 'platejs/react';
import { useEditorRef, useElement, usePluginOption } from 'platejs/react';
import { Link, useMatch } from 'react-router-dom';
import { useCreateSkillAndEdit } from 'src/components/admin/agent-personalization/use-create-skill-and-edit';
import { useCreateSkillWithAi } from 'src/components/admin/agent-personalization/use-create-skill-with-ai';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { Apollo } from 'src/gql/generated';
import { pushNotification } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { SKILL_SCOPE_DESCRIPTIONS, SKILL_SCOPE_ICONS } from './skill-appearance';
import {
  getIsSkillScopeOffered,
  getPinnedSkillsFromEditor,
  getSkillPinRefusal,
  insertSkillElement,
  SKILL_PIN_REFUSAL_MESSAGES,
} from './utils';
import { BaseSkillElementPlugin } from '../../AlineMarkdownEditor/plugins/skill-element-plugin';
import type { PickerKeyboardEvent } from '../ChatEditorTriggerPicker';
import {
  getContextPickerOrigin,
  keepPickerFocus,
  OPENED_WITHOUT_TYPING,
  stopPickerKeyboardEvent,
  TriggerPickerInput,
  useTriggerPicker,
} from '../ChatEditorTriggerPicker';
import { SKILL_PICKER_VIEWS } from '../ChatEditorTriggerPicker/trigger-origin';

const MENU_WIDTH = 360;
const NEW_SKILL_OPTION_COUNT = 2;
/** A skill name never starts with a space, so a slash followed by one is prose. */
const LEADING_WHITESPACE_PATTERN = /^\s/;
const WHITESPACE_PATTERN = /\s/;

interface SkillOption {
  description: string;
  id: string;
  name: string;
  scope: Apollo.AgentSkillScope;
  /** A personal skill listed in a shared workflow or task, where it cannot run. */
  unavailable: boolean;
  versionId: string;
}

const PERSONAL_SKILL_SHARED_CONTEXT_MESSAGE =
  'Personal skills are private to you, so a workflow or task cannot use them. Share this one with your organization to add it here.';

const useStyles = makeStyles()(theme => ({
  empty: {
    color: theme.appColors.iconsType,
    padding: theme.spacing(1.5),
  },
  groupHeader: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 700,
    padding: theme.spacing(0.25, 1),
    textTransform: 'uppercase',
  },
  groupHeaderWithBack: {
    alignItems: 'center',
    display: 'flex',
    gap: 6,
  },
  headerButton: {
    // No hover fill on the grey header; the icon darkens instead.
    '&:hover': { backgroundColor: 'transparent', color: theme.palette.text.primary },
    color: theme.appColors.iconsType,
    flexShrink: 0,
    height: 20,
    padding: 0,
    width: 20,
  },
  icon: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  item: {
    '&:hover': { backgroundColor: theme.appColors.hoverBackground },
    alignItems: 'center',
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 1),
  },
  itemDisabled: {
    cursor: 'default',
  },
  itemHighlighted: {
    backgroundColor: theme.appColors.selectedBackground,
  },
  itemText: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
  },
  itemUnavailable: {
    opacity: 0.5,
  },
  manageSkillsButton: {
    marginLeft: 'auto',
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    overflowY: 'auto',
    padding: 0,
    width: MENU_WIDTH,
    zIndex: theme.zIndex.tooltip,
  },
  menuButton: {
    '&:hover': { backgroundColor: theme.appColors.hoverBackground },
    borderRadius: 4,
    color: theme.palette.text.primary,
    display: 'flex',
    fontSize: 14,
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
    padding: theme.spacing(0.5, 1),
    width: '100%',
  },
  // Matches the `@` menu's rows: a 20px icon box, then 14px text.
  newSkillOption: {
    lineHeight: '20px',
  },
  optionLabel: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  scope: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: 12,
    gap: theme.spacing(0.5),
    whiteSpace: 'nowrap',
  },
  title: {
    fontSize: 14,
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const BackButton = React.memo(function BackButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  const { classes } = useStyles();

  return (
    <IconButton
      aria-label={label}
      className={classes.headerButton}
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
      onMouseDown={keepPickerFocus}
    >
      <ArrowLeft size={14} />
    </IconButton>
  );
});

const ManageSkillsButton = React.memo(function ManageSkillsButton({
  onClick,
}: {
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  const { classes } = useStyles();

  return (
    <AlineTooltip arrow placement="top" title="Manage skills">
      <IconButton
        aria-label="Manage skills"
        className={clsx(classes.headerButton, classes.manageSkillsButton)}
        component={Link}
        to={link.AdminAgentSkillIndex()}
        onClick={onClick}
        onMouseDown={keepPickerFocus}
      >
        <Settings size={14} />
      </IconButton>
    </AlineTooltip>
  );
});

const NewSkillMenu = React.memo(function NewSkillMenu({
  backLabel,
  disabled,
  highlightedIndex,
  onBack,
  onCreateBlank,
  onCreateWithAi,
  onHighlight,
}: {
  backLabel: string;
  disabled: boolean;
  highlightedIndex: number;
  onBack: () => void;
  onCreateBlank: () => void;
  onCreateWithAi: () => void;
  onHighlight: (index: number) => void;
}) {
  const { classes } = useStyles();
  const options = [
    { action: onCreateBlank, icon: FilePlus, label: 'Create a blank skill' },
    { action: onCreateWithAi, icon: Sparkles, label: 'Create a skill with AI' },
  ];

  return (
    <>
      <div className={clsx(classes.groupHeader, classes.groupHeaderWithBack)}>
        <BackButton label={backLabel} onClick={onBack} />
        New skill
      </div>
      {options.map((option, index) => (
        <ButtonBase
          key={option.label}
          aria-label={option.label}
          className={clsx(
            classes.menuButton,
            classes.newSkillOption,
            highlightedIndex === index && classes.itemHighlighted
          )}
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            option.action();
          }}
          onMouseDown={keepPickerFocus}
          onMouseEnter={() => onHighlight(index)}
        >
          <span className={classes.icon}>
            <option.icon size={16} />
          </span>
          <span className={classes.optionLabel}>{option.label}</span>
        </ButtonBase>
      ))}
    </>
  );
});
/**
 * A disabled row still takes the click, so the picker can say why the skill cannot be
 * added instead of doing nothing.
 */
const SkillRow = React.memo(function SkillRow({
  disabled,
  highlighted,
  index,
  onHighlight,
  onSelect,
  pinned,
  skill,
}: {
  disabled: boolean;
  highlighted: boolean;
  index: number;
  onHighlight: (index: number) => void;
  onSelect: (skill: SkillOption) => void;
  pinned: boolean;
  skill: SkillOption;
}) {
  const { classes } = useStyles();
  const ScopeIcon = SKILL_SCOPE_ICONS[skill.scope];
  const scopeDescription = SKILL_SCOPE_DESCRIPTIONS[skill.scope];
  // Hovering the scope icon swaps the row's description tooltip for the scope one.
  const [scopeHovered, setScopeHovered] = useState(false);
  // The docked chat sits on the right edge, so its description opens toward the page.
  const isFullPageChat = Boolean(useMatch({ end: false, path: routes.ChatIndex }));

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      onSelect(skill);
    },
    [onSelect, skill]
  );
  const handleMouseEnter = useCallback(() => onHighlight(index), [index, onHighlight]);

  return (
    <AlineTooltip
      arrow
      placement={isFullPageChat ? 'right' : 'left'}
      title={getSkillRowTooltip(skill, scopeHovered)}
    >
      <div
        aria-disabled={disabled}
        className={clsx(
          classes.item,
          disabled && classes.itemDisabled,
          skill.unavailable && classes.itemUnavailable,
          highlighted && classes.itemHighlighted
        )}
        role="button"
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
      >
        <span className={classes.icon}>
          <Sparkles size={16} strokeWidth={2} />
        </span>
        <div className={classes.itemText}>
          <Typography className={classes.title} variant="body2">
            {skill.name}
          </Typography>
        </div>
        {pinned && (
          <span className={classes.scope}>
            <Check size={12} />
            Added
          </span>
        )}
        <AlineTooltip arrow placement="top" title={scopeDescription}>
          <span
            aria-label={scopeDescription}
            className={classes.scope}
            onMouseEnter={() => setScopeHovered(true)}
            onMouseLeave={() => setScopeHovered(false)}
          >
            <ScopeIcon size={12} />
          </span>
        </AlineTooltip>
      </div>
    </AlineTooltip>
  );
});

const SkillList = React.memo(function SkillList({
  highlightedIndex,
  isAtSkillLimit,
  onBack,
  onHighlight,
  onManageSkillsClick,
  onOpenNewSkillMenu,
  onSelect,
  pinnedSkillIds,
  skills,
}: {
  highlightedIndex: number;
  isAtSkillLimit: boolean;
  onHighlight: (index: number) => void;
  onManageSkillsClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onSelect: (skill: SkillOption) => void;
  pinnedSkillIds: Set<string>;
  skills: SkillOption[];
  /** Set when the list was opened from the `@` picker, to step back to it. */
  onBack?: () => void;
  onOpenNewSkillMenu?: () => void;
}) {
  const { classes } = useStyles();
  // `skills` lists own and organization skills before Community ones, so one index runs through both.
  const ownSkills = skills.filter(skill => !getIsCommunitySkill(skill));
  const communitySkills = skills.filter(getIsCommunitySkill);
  const renderRow = (skill: SkillOption, index: number) => (
    <SkillRow
      key={skill.id}
      disabled={pinnedSkillIds.has(skill.id) || isAtSkillLimit || skill.unavailable}
      highlighted={index === highlightedIndex}
      index={index}
      pinned={pinnedSkillIds.has(skill.id)}
      skill={skill}
      onHighlight={onHighlight}
      onSelect={onSelect}
    />
  );

  return (
    <>
      {onOpenNewSkillMenu && (
        <>
          <div className={classes.groupHeader}>Add</div>
          <ButtonBase
            className={classes.menuButton}
            onClick={event => {
              event.stopPropagation();
              onOpenNewSkillMenu();
            }}
            onMouseDown={keepPickerFocus}
          >
            <span className={classes.icon}>
              <Plus size={18} />
            </span>
            New skill
          </ButtonBase>
        </>
      )}
      <div className={clsx(classes.groupHeader, classes.groupHeaderWithBack)}>
        {onBack && <BackButton label="Back to context" onClick={onBack} />}
        My skills
        <ManageSkillsButton onClick={onManageSkillsClick} />
      </div>
      {isAtSkillLimit && (
        <Typography className={classes.empty} variant="body2">
          You can add up to {MAX_PINNED_TURN_SKILLS} skills to a message.
        </Typography>
      )}
      {ownSkills.map((skill, index) => renderRow(skill, index))}
      {Boolean(communitySkills.length) && (
        <div className={classes.groupHeader}>Community skills</div>
      )}
      {communitySkills.map((skill, index) => renderRow(skill, ownSkills.length + index))}
      {!skills.length && (
        <Typography className={classes.empty} variant="body2">
          No skills match.
        </Typography>
      )}
    </>
  );
});

/**
 * Inline picker opened by typing `/`.
 *
 * Shares its trigger mechanics with the `@` mention picker through `useTriggerPicker`, so
 * only the data and the rows live here. The list is filtered client-side because a user's
 * skill library is small. Unlike mentions, there is no server-side search to debounce.
 *
 * It lists every complete skill the user can read, including ones marked
 * `disable-model-invocation`: picking one by hand is exactly how those are meant to be used. An
 * editor for text that someone else runs, such as a workflow chat or a task description, sets
 * `offeredScopes` to the skills that run can read. Personal skills stay in the list there,
 * disabled, because they are private to their author. A new skill starts personal, so that
 * editor also has no way to make one.
 */
export const SkillInputElement = React.memo(function SkillInputElement(
  props: PlateElementProps<TElement>
): React.JSX.Element {
  const element = useElement();
  const editor = useEditorRef();
  const { classes } = useStyles();
  const { createSkillWithAi } = useCreateSkillWithAi();
  const contextOrigin = getContextPickerOrigin(element);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const { createSkillAndEdit, loading } = useCreateSkillAndEdit();
  const openedOnNewSkill = contextOrigin?.initialView === SKILL_PICKER_VIEWS.NewSkill;
  const [newSkillMenuRequested, setNewSkillMenuRequested] = useState(openedOnNewSkill);
  const { data } = Apollo.useGetAgentSkillsQuery({ fetchPolicy: 'cache-and-network' });
  const offeredScopes = usePluginOption(BaseSkillElementPlugin, 'offeredScopes') ?? null;
  // A new skill starts personal, so only a picker that offers personal skills can make one.
  const canCreate = getIsSkillScopeOffered(Apollo.AgentSkillScope.Personal, offeredScopes);
  /**
   * Derived rather than reset, so a picker opened on New skill keeps that screen while the
   * thread's skill audience is still loading.
   */
  const newSkillMenuOpen = canCreate && newSkillMenuRequested;

  const pinnedSkills = getPinnedSkillsFromEditor(editor);
  const pinnedSkillIds = new Set(pinnedSkills.map(pinned => pinned.skillId));
  const isAtSkillLimit = pinnedSkills.length >= MAX_PINNED_TURN_SKILLS;
  /**
   * An unfinished or never-published skill is hidden: the server leaves it out of the agent's
   * catalog too, and there is no version to pin.
   */
  const allSkills = useMemo<SkillOption[]>(
    () =>
      (data?.agentSkills ?? []).flatMap(({ currentVersion, id, isComplete, scope }) => {
        if (!currentVersion || !isComplete) return [];
        const offered = getIsSkillScopeOffered(scope, offeredScopes);
        // A personal skill the run cannot read stays visible, so the row can say why.
        if (!offered && scope !== Apollo.AgentSkillScope.Personal) return [];
        return [
          {
            description: currentVersion.description,
            id,
            name: currentVersion.name,
            scope,
            unavailable: !offered,
            versionId: currentVersion.id,
          },
        ];
      }),
    [data, offeredScopes]
  );

  const picker = useTriggerPicker({
    element,
    onEmptyDelete: handleEmptyDelete,
    onNavigationKeyDown: handleNavigationKeyDown,
    trigger: '/',
  });
  const { dismissAsText, focusEditable, query, removeTriggerAndRestoreCaret } = picker;

  const backToContext = useCallback(() => {
    if (!contextOrigin) return;
    removeTriggerAndRestoreCaret();
    editor.tf.insertNodes({
      children: [{ text: contextOrigin.query }],
      ...(contextOrigin.typed ? {} : OPENED_WITHOUT_TYPING),
      contextSessionId: contextOrigin.sessionId,
      type: MentionInputPlugin.key,
    });
  }, [contextOrigin, editor, removeTriggerAndRestoreCaret]);

  // Grouped as the list shows them, so the highlight index and the rows agree.
  const skills = useMemo(() => {
    const matched = getMatchingSkills(allSkills, query);
    const mine = matched.filter(skill => !getIsCommunitySkill(skill));
    // Usable skills first, so a disabled personal skill does not sit above one the run can use.
    return [
      ...mine.filter(skill => !skill.unavailable),
      ...mine.filter(skill => skill.unavailable),
      ...matched.filter(getIsCommunitySkill),
    ];
  }, [allSkills, query]);

  // A new query starts the highlight over at the top of its results.
  const [highlightedQuery, setHighlightedQuery] = useState(query);
  if (highlightedQuery !== query) {
    setHighlightedQuery(query);
    setHighlightedIndex(0);
  }

  /**
   * A slash followed by words nothing matches is prose, as in "/api/v1 is down" or
   * "and / or". Hand it back to the editor as typed rather than keep swallowing the
   * sentence into the search box.
   */
  const isProse =
    !newSkillMenuOpen &&
    (LEADING_WHITESPACE_PATTERN.test(query) ||
      (Boolean(data) && WHITESPACE_PATTERN.test(query) && !skills.length));

  const selectSkill = useCallback(
    (skill: SkillOption) => {
      if (skill.unavailable) {
        pushNotification({
          key: uuid4(),
          message: PERSONAL_SKILL_SHARED_CONTEXT_MESSAGE,
          variant: 'info',
        });
        return;
      }
      const refusal = getSkillPinRefusal({
        pinned: getPinnedSkillsFromEditor(editor),
        skillId: skill.id,
      });
      if (refusal) {
        pushNotification({
          key: uuid4(),
          message: SKILL_PIN_REFUSAL_MESSAGES[refusal],
          variant: 'info',
        });
        return;
      }
      removeTriggerAndRestoreCaret();
      insertSkillElement(editor, {
        description: skill.description,
        name: skill.name,
        skillId: skill.id,
        versionId: skill.versionId,
      });
      focusEditable();
    },
    [editor, focusEditable, removeTriggerAndRestoreCaret]
  );

  const createBlank = useCallback(() => {
    if (loading) return;
    removeTriggerAndRestoreCaret();
    void createSkillAndEdit();
  }, [createSkillAndEdit, loading, removeTriggerAndRestoreCaret]);

  const createWithAi = useCallback(() => {
    removeTriggerAndRestoreCaret();
    createSkillWithAi();
  }, [createSkillWithAi, removeTriggerAndRestoreCaret]);

  const openNewSkillMenu = useCallback(() => {
    setHighlightedIndex(0);
    setNewSkillMenuRequested(true);
  }, []);

  // The New skill screen steps back to wherever it was opened from: `@` or the skill list.
  const leaveNewSkillMenu = useCallback(() => {
    if (openedOnNewSkill) {
      backToContext();
      return;
    }
    setNewSkillMenuRequested(false);
    setHighlightedIndex(0);
  }, [backToContext, openedOnNewSkill]);

  /**
   * Declared after `picker` because it needs the query-filtered list; the hook reads it
   * through a ref, so each render's copy is the one that runs. An empty list lets the keys
   * fall through to the editor rather than swallowing them.
   */
  function handleNavigationKeyDown(event: PickerKeyboardEvent): boolean {
    if (newSkillMenuOpen && event.key === 'ArrowLeft') {
      stopPickerKeyboardEvent(event);
      leaveNewSkillMenu();
      return true;
    }
    const optionCount = newSkillMenuOpen ? NEW_SKILL_OPTION_COUNT : skills.length;
    if (!optionCount) return false;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        stopPickerKeyboardEvent(event);
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setHighlightedIndex(current => (current + step + optionCount) % optionCount);
        return true;
      }
      case 'Enter':
      case 'Tab': {
        if (newSkillMenuOpen) {
          stopPickerKeyboardEvent(event);
          if (highlightedIndex === 0) createBlank();
          else createWithAi();
          return true;
        }
        const skill = skills.at(highlightedIndex);
        if (!skill) return false;
        stopPickerKeyboardEvent(event);
        selectSkill(skill);
        return true;
      }
    }
    return false;
  }

  // An empty Backspace steps up one level before it removes the `/`.
  function handleEmptyDelete(): boolean {
    if (newSkillMenuOpen) {
      leaveNewSkillMenu();
      return true;
    }
    if (!contextOrigin) return false;

    backToContext();
    return true;
  }

  const handleManageSkillsClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
      removeTriggerAndRestoreCaret();
    },
    [removeTriggerAndRestoreCaret]
  );

  useEffect(() => {
    if (!isProse) return;
    dismissAsText();
    focusEditable();
  }, [dismissAsText, focusEditable, isProse]);

  return (
    <TriggerPickerInput
      ariaLabel="Search skills"
      elementProps={props}
      menu={
        <>
          {newSkillMenuOpen && (
            <NewSkillMenu
              backLabel={openedOnNewSkill ? 'Back to context' : 'Back to skills'}
              disabled={loading}
              highlightedIndex={highlightedIndex}
              onBack={leaveNewSkillMenu}
              onCreateBlank={createBlank}
              onCreateWithAi={createWithAi}
              onHighlight={setHighlightedIndex}
            />
          )}
          {!newSkillMenuOpen && (
            <SkillList
              highlightedIndex={highlightedIndex}
              isAtSkillLimit={isAtSkillLimit}
              pinnedSkillIds={pinnedSkillIds}
              skills={skills}
              onBack={contextOrigin ? backToContext : undefined}
              onHighlight={setHighlightedIndex}
              onManageSkillsClick={handleManageSkillsClick}
              onOpenNewSkillMenu={canCreate ? openNewSkillMenu : undefined}
              onSelect={selectSkill}
            />
          )}
        </>
      }
      menuClassName={classes.menu}
      picker={picker}
      trigger="/"
    />
  );
});

function getSkillRowTooltip(skill: SkillOption, scopeHovered: boolean): string {
  if (scopeHovered) return '';
  if (skill.unavailable) return PERSONAL_SKILL_SHARED_CONTEXT_MESSAGE;
  return skill.description;
}

function getIsCommunitySkill(skill: SkillOption): boolean {
  return skill.scope === Apollo.AgentSkillScope.Community;
}

/** Name matches ranked first, then description matches. Whitespace searches match literally. */
function getMatchingSkills(skills: SkillOption[], query: string): SkillOption[] {
  if (!query) return skills;
  const lower = query.toLowerCase();
  const literal = skills.filter(
    skill =>
      skill.name.toLowerCase().includes(lower) || skill.description.toLowerCase().includes(lower)
  );
  if (WHITESPACE_PATTERN.test(query)) return literal;
  const names = matchSorter(skills, query, { keys: ['name'] });
  const matchedNames = new Set(names.map(skill => skill.id));
  return [...names, ...literal.filter(skill => !matchedNames.has(skill.id))];
}
